// The door-side readers of the tier, billing and trial values the app injects
// through `configureCommerceData` (ruling 65,
// `docs/monorepo-ui-floor-rulings/65-identity-crosses-by-injection.md`).
//
// Headless by construction: no design system, no router, no store, no `@/`.
// None of them reuses a name this door already publishes — a reader repointed
// onto `useQueryUserBillingInfo` would compile, pass every test and silently
// drop the delegate's dev override, `enabled` composition and error toast,
// which is what `@heidi/rules/no-same-name-door-codemod` refuses.

import {
  getCommerceDataConfig,
  type CommerceDataConfig,
  type ReverseTrialCounters,
  type UserBillingInfoSubscription,
  type UserTierView,
} from './config';
import type { QueryUserBillingInfoResponse } from './query/useQueryUserBillingInfo';

/**
 * Fails loudly and BY NAME. `getCommerceDataConfig` only knows whether the
 * seam was configured at all, so an un-injected member would otherwise reach a
 * consumer as `undefined` and blow up somewhere with no member in the message.
 */
const injected = <K extends keyof CommerceDataConfig>(
  member: K
): NonNullable<CommerceDataConfig[K]> => {
  const value = getCommerceDataConfig()[member];
  if (value === undefined) {
    throw new Error(
      `[commerce/data] \`${member}\` is not configured — the app injects it at ` +
        'src/utils/commerce-data-wiring.ts (ruling 65); a test exercising this ' +
        'reader must configure a double for that member first.'
    );
  }
  return value;
};

/** The flag-gated tier derivation, resolved app-side. */
export const useUserTier = (): UserTierView => injected('useUserTier')();

/** The app `useQueryUserBillingInfo` delegate, reactions included. */
export const useUserBillingInfo = <T = QueryUserBillingInfoResponse>(
  select?: (data: QueryUserBillingInfoResponse) => T
): UserBillingInfoSubscription<T> => injected('useUserBillingInfo')(select);

/** The reverse-trial booleans and counts. The copy stays app-side. */
export const useReverseTrial = (): ReverseTrialCounters => injected('useReverseTrial')();
